import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useWebinars } from "../../hooks/useWebinars"
import { useSpeakers } from "../../hooks/useSpeakers"
import { useWebinarTranslations } from "../../hooks/useWebinarTranslations"
import { Button } from "../../components/ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../../components/Card"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Textarea } from "../../components/ui/textarea"
import ImageUpload from "../../components/ImageUpload"
import { Badge } from "../../components/ui/badge"
import WebinarTranslationForm from "../../components/webinar/WebinarTranslationForm"
import { ArrowLeft, Image as ImageIcon, UserPlus, X, Plus } from "lucide-react"

export default function CreateWebinarPage() {
  const navigate = useNavigate()
  const { createWebinar, loading } = useWebinars()
  const { getSpeakers, createSpeaker, loading: loadingSpeaker } = useSpeakers()
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    meetLink: "",
    videoLink: "",
    thumbnail: "",
    duration: "",
    maxParticipants: "",
    language: "fr",
    isPublished: false
  })
  const [thumbnailFile, setThumbnailFile] = useState(null)
  const [dateError, setDateError] = useState("")
  const [selectedSpeakerIds, setSelectedSpeakerIds] = useState([])
  const [availableSpeakers, setAvailableSpeakers] = useState([])
  const [loadingSpeakers, setLoadingSpeakers] = useState(false)
  const [showNewSpeakerForm, setShowNewSpeakerForm] = useState(false)
  const [newSpeakerData, setNewSpeakerData] = useState({
    name: "",
    title: "",
    photo: ""
  })
  const [newSpeakerPhotoFile, setNewSpeakerPhotoFile] = useState(null)
  const [preparedTranslations, setPreparedTranslations] = useState([])
  const { createWebinarTranslation } = useWebinarTranslations()

  // Charger les intervenants existants
  useEffect(() => {
    const loadSpeakers = async () => {
      setLoadingSpeakers(true)
      try {
        const response = await getSpeakers({ page: 1, limit: 100 })
        setAvailableSpeakers(response?.data || [])
      } catch (error) {
        console.error("Erreur lors du chargement des intervenants:", error)
      } finally {
        setLoadingSpeakers(false)
      }
    }
    loadSpeakers()
  }, [])

  // Obtenir la date minimale (maintenant) au format datetime-local
  const getMinDateTime = () => {
    const now = new Date()
    // Ajuster pour le fuseau horaire local
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  const handleAddNewSpeaker = async () => {
    if (!newSpeakerData.name) {
      alert("Le nom de l'intervenant est obligatoire")
      return
    }

    try {
      const speakerPayload = {
        name: newSpeakerData.name,
        ...(newSpeakerData.title && { title: newSpeakerData.title }),
        ...(newSpeakerPhotoFile && { photoFile: newSpeakerPhotoFile })
      }
      const newSpeaker = await createSpeaker(speakerPayload)
      
      // Ajouter le nouvel intervenant à la liste disponible et le sélectionner
      setAvailableSpeakers([...availableSpeakers, newSpeaker])
      setSelectedSpeakerIds([...selectedSpeakerIds, newSpeaker.id])
      
      // Réinitialiser le formulaire
      setNewSpeakerData({ name: "", title: "", photo: "" })
      setNewSpeakerPhotoFile(null)
      setShowNewSpeakerForm(false)
    } catch (error) {
      // L'erreur est déjà gérée dans le hook
    }
  }

  const handleToggleSpeaker = (speakerId) => {
    if (selectedSpeakerIds.includes(speakerId)) {
      setSelectedSpeakerIds(selectedSpeakerIds.filter(id => id !== speakerId))
    } else {
      setSelectedSpeakerIds([...selectedSpeakerIds, speakerId])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Valider que la date n'est pas dans le passé
    if (formData.date) {
      const selectedDate = new Date(formData.date)
      const now = new Date()
      if (selectedDate < now) {
        setDateError("La date et l'heure du webinaire ne peuvent pas être dans le passé")
        return
      }
    }
    
    setDateError("")
    
    try {
      const webinarData = {
        title: formData.title,
        description: formData.description,
        date: new Date(formData.date).toISOString(),
        meetLink: formData.meetLink,
        ...(formData.videoLink && { videoLink: formData.videoLink }),
        ...(thumbnailFile && { thumbnailFile }), // Fichier uploadé
        ...(!thumbnailFile && formData.thumbnail && { thumbnail: formData.thumbnail }), // URL si pas de fichier
        ...(formData.duration && { duration: parseInt(formData.duration) }),
        ...(formData.maxParticipants && { maxParticipants: parseInt(formData.maxParticipants) }),
        language: "fr", // Toujours français par défaut
        ...(selectedSpeakerIds.length > 0 && { speakerIds: selectedSpeakerIds }),
        isPublished: formData.isPublished
      }
      
      // Créer le webinaire
      const newWebinar = await createWebinar(webinarData)
      
      // Créer les traductions préparées
      if (preparedTranslations.length > 0 && newWebinar?.id) {
        try {
          await Promise.all(
            preparedTranslations.map(translation =>
              createWebinarTranslation(newWebinar.id, translation)
            )
          )
        } catch (translationError) {
          console.error("Erreur lors de la création des traductions:", translationError)
          // Ne pas bloquer la navigation même si les traductions échouent
        }
      }
      
      navigate("/admin/webinars")
    } catch (error) {
      // L'erreur est déjà gérée dans le hook
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Valider la date en temps réel
    if (name === 'date' && value) {
      const selectedDate = new Date(value)
      const now = new Date()
      if (selectedDate < now) {
        setDateError("La date et l'heure du webinaire ne peuvent pas être dans le passé")
      } else {
        setDateError("")
      }
    }
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader title="Créer un webinaire" />
        
        <Button
          variant="ghost"
          onClick={() => navigate("/admin/webinars")}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>Informations du webinaire</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Titre */}
              <div>
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="Introduction au Machine Learning"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Découvrez les bases du Machine Learning..."
                />
              </div>

              {/* Date et heure */}
              <div>
                <Label htmlFor="date">Date et heure *</Label>
                <Input
                  id="date"
                  name="date"
                  type="datetime-local"
                  value={formData.date}
                  onChange={handleChange}
                  min={getMinDateTime()}
                  required
                  className={dateError ? "border-red-500" : ""}
                />
                {dateError && (
                  <p className="mt-1 text-sm text-red-600">{dateError}</p>
                )}
              </div>

              {/* Lien de la réunion */}
              <div>
                <Label htmlFor="meetLink">Lien de la réunion (Google Meet, Zoom, etc.) *</Label>
                <Input
                  id="meetLink"
                  name="meetLink"
                  type="url"
                  value={formData.meetLink}
                  onChange={handleChange}
                  required
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                />
              </div>

              {/* Lien vidéo (optionnel) */}
              <div>
                <Label htmlFor="videoLink">Lien vidéo replay (optionnel)</Label>
                <Input
                  id="videoLink"
                  name="videoLink"
                  type="url"
                  value={formData.videoLink}
                  onChange={handleChange}
                  placeholder="https://www.youtube.com/watch?v=xxxxx"
                />
              </div>

              {/* Image de couverture (optionnel) */}
              <div>
                <Label htmlFor="thumbnail" className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-4 h-4" />
                  Image de couverture (optionnel)
                </Label>
                <ImageUpload
                  onImageSelect={(file) => {
                    setThumbnailFile(file)
                    // Si un fichier est sélectionné, effacer l'URL
                    if (file) {
                      setFormData(prev => ({ ...prev, thumbnail: "" }))
                    }
                  }}
                  currentImage={formData.thumbnail || (thumbnailFile ? URL.createObjectURL(thumbnailFile) : null)}
                  placeholder="Cliquez pour sélectionner une image de couverture"
                />
                {thumbnailFile && (
                  <p className="text-green-600 text-xs mt-2">
                    {thumbnailFile.name} ({(thumbnailFile.size / 1024).toFixed(2)} KB)
                  </p>
                )}
                {!thumbnailFile && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-2">Ou entrez une URL :</p>
                    <Input
                      id="thumbnail"
                      name="thumbnail"
                      type="url"
                      value={formData.thumbnail}
                      onChange={handleChange}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                )}
                <p className="text-gray-500 text-xs mt-2">
                  Format recommandé : JPG, PNG, GIF, WEBP. Taille max : 10 MB. Taille recommandée : 1200x675px (16:9)
                </p>
              </div>

              {/* Durée (optionnel) */}
              <div>
                <Label htmlFor="duration">Durée en minutes (optionnel)</Label>
                <Input
                  id="duration"
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleChange}
                  min="1"
                  placeholder="90"
                />
              </div>

              {/* Nombre max de participants (optionnel) */}
              <div>
                <Label htmlFor="maxParticipants">Nombre maximum de participants (optionnel, laisser vide pour illimité)</Label>
                <Input
                  id="maxParticipants"
                  name="maxParticipants"
                  type="number"
                  value={formData.maxParticipants}
                  onChange={handleChange}
                  min="1"
                  placeholder="100"
                />
              </div>

              {/* Langue par défaut */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Langue par défaut :</strong> Français (vous pourrez ajouter des traductions dans d'autres langues ci-dessous)
                </p>
              </div>

              {/* Intervenants (optionnel) */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <Label className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Intervenants (optionnel)
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewSpeakerForm(!showNewSpeakerForm)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un intervenant
                  </Button>
                </div>

                {/* Formulaire de création d'intervenant */}
                {showNewSpeakerForm && (
                  <div className="border rounded-lg p-4 bg-white mb-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium">Nouvel intervenant</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowNewSpeakerForm(false)
                          setNewSpeakerData({ name: "", title: "", photo: "" })
                          setNewSpeakerPhotoFile(null)
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="newSpeakerName">Nom *</Label>
                        <Input
                          id="newSpeakerName"
                          value={newSpeakerData.name}
                          onChange={(e) => setNewSpeakerData({ ...newSpeakerData, name: e.target.value })}
                          required
                          placeholder="Dr. Marie Dupont"
                        />
                      </div>
                      <div>
                        <Label htmlFor="newSpeakerTitle">Fonction</Label>
                        <Input
                          id="newSpeakerTitle"
                          value={newSpeakerData.title}
                          onChange={(e) => setNewSpeakerData({ ...newSpeakerData, title: e.target.value })}
                          placeholder="Experte en Intelligence Artificielle"
                        />
                      </div>
                      <div>
                        <Label htmlFor="newSpeakerPhoto" className="flex items-center gap-2 mb-2">
                          <ImageIcon className="w-4 h-4" />
                          Photo (optionnel)
                        </Label>
                        <ImageUpload
                          onImageSelect={(file) => {
                            setNewSpeakerPhotoFile(file)
                            if (file) {
                              setNewSpeakerData(prev => ({ ...prev, photo: "" }))
                            }
                          }}
                          currentImage={newSpeakerData.photo || (newSpeakerPhotoFile ? URL.createObjectURL(newSpeakerPhotoFile) : null)}
                          placeholder="Cliquez pour sélectionner une photo"
                        />
                        {newSpeakerPhotoFile && (
                          <p className="text-green-600 text-xs mt-2">
                            {newSpeakerPhotoFile.name} ({(newSpeakerPhotoFile.size / 1024).toFixed(2)} KB)
                          </p>
                        )}
                        {!newSpeakerPhotoFile && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-500 mb-2">Ou entrez une URL :</p>
                            <Input
                              id="newSpeakerPhoto"
                              type="url"
                              value={newSpeakerData.photo}
                              onChange={(e) => setNewSpeakerData({ ...newSpeakerData, photo: e.target.value })}
                              placeholder="https://example.com/photo.jpg"
                            />
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={handleAddNewSpeaker}
                        disabled={loadingSpeaker || !newSpeakerData.name}
                        size="sm"
                      >
                        {loadingSpeaker ? "Création..." : "Ajouter l'intervenant"}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Liste des intervenants disponibles */}
                {loadingSpeakers ? (
                  <p className="text-sm text-gray-500">Chargement des intervenants...</p>
                ) : availableSpeakers.length > 0 ? (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Sélectionner des intervenants existants :</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {availableSpeakers.map((speaker) => (
                        <div
                          key={speaker.id}
                          className={`flex items-center space-x-2 p-2 border rounded cursor-pointer transition-colors ${
                            selectedSpeakerIds.includes(speaker.id)
                              ? "bg-[#203a8a] text-white border-[#203a8a]"
                              : "bg-white hover:bg-gray-100"
                          }`}
                          onClick={() => handleToggleSpeaker(speaker.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSpeakerIds.includes(speaker.id)}
                            onChange={() => handleToggleSpeaker(speaker.id)}
                            className="w-4 h-4"
                          />
                          {speaker.photo && (
                            <img
                              src={speaker.photo}
                              alt={speaker.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{speaker.name}</p>
                            {speaker.title && (
                              <p className={`text-xs truncate ${
                                selectedSpeakerIds.includes(speaker.id) ? "text-gray-200" : "text-gray-500"
                              }`}>
                                {speaker.title}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucun intervenant disponible. Créez-en un pour commencer.</p>
                )}

                {/* Intervenants sélectionnés */}
                {selectedSpeakerIds.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <Label className="text-sm font-medium mb-2 block">Intervenants sélectionnés :</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedSpeakerIds.map((speakerId) => {
                        const speaker = availableSpeakers.find(s => s.id === speakerId)
                        if (!speaker) return null
                        return (
                          <Badge
                            key={speakerId}
                            className="bg-[#203a8a] text-white flex items-center gap-2"
                          >
                            {speaker.name}
                            {speaker.title && ` - ${speaker.title}`}
                            <button
                              type="button"
                              onClick={() => handleToggleSpeaker(speakerId)}
                              className="ml-1 hover:bg-[#1a1f4a] rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Publication */}
              <div className="flex items-center space-x-2">
                <input
                  id="isPublished"
                  name="isPublished"
                  type="checkbox"
                  checked={formData.isPublished}
                  onChange={handleChange}
                  className="w-4 h-4"
                />
                <Label htmlFor="isPublished" className="cursor-pointer">
                  Publier immédiatement
                </Label>
              </div>

              {/* Boutons */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#203a8a] text-white hover:bg-[#1a1f4a]"
                >
                  {loading ? "Création..." : "Créer le webinaire"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/admin/webinars")}
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Gestion des traductions */}
        <div className="mt-6">
          <WebinarTranslationForm
            defaultLanguage="fr"
            translations={preparedTranslations}
            onChange={setPreparedTranslations}
          />
        </div>
      </div>
    </div>
  )
}

